import { Injectable } from '@angular/core';

import { isEqual } from 'lodash-es';
import { PartialMetadata } from '../../../core/records/entities/metadata/partial-metadata.entity';
import { PartialRecord } from '../../../core/records/entities/partial-record.class';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';
import { isNonNullSimpleFieldValue } from '../fields/field-validation.helper';

@Injectable({ providedIn: 'root' })
export class RecordFieldValueHelper {
    public getPropertyList<
        F extends keyof NonNullable<PartialMetadata>,
        P extends keyof NonNullable<NonNullable<PartialMetadata>[F]>
    >(family: F, property: P, records: PartialRecord[]): NonNullable<PartialMetadata[F]>[P][] {
        const mapped: NonNullable<PartialMetadata[F]>[P][] = records.map(record => record.get(family, property));
        return mapped;
    }

    public getSubPropertyList<
        F extends keyof NonNullable<PartialMetadata>,
        P extends keyof NonNullable<NonNullable<PartialMetadata>[F]>,
        Q extends keyof NonNullable<NonNullable<NonNullable<PartialMetadata>[F]>[P]>
    >(
        family: F,
        property: P,
        subproperty: Q,
        records: PartialRecord[],
        fallback: NonNullable<NonNullable<PartialMetadata[F]>[P]>[Q]
    ): NonNullable<NonNullable<PartialMetadata[F]>[P]>[Q][] {
        const mapped: NonNullable<NonNullable<PartialMetadata[F]>[P]>[Q][] = records.map(record => {
            const result: NonNullable<NonNullable<PartialMetadata[F]>[P]>[Q] = record.get2(
                family,
                property,
                subproperty
            );
            if (result === null) {
                return fallback;
            }
            return result;
        });
        return mapped;
    }

    public mergeMap<T>(maps: T[][]): FormValue<T>[] {
        const intersection: T[] = this.intersection(maps);
        const disjunction: T[] = this.inverseIntersection(maps);

        if (disjunction.length === 0) {
            const intersectionFormValues: FormValue<T>[] = intersection.map(
                el => new FormValue(el, FormValueState.SET)
            );
            return intersectionFormValues;
        }

        // Return whole object as INDETERMINATE if any value is Indeterminate
        const indeterminateFormValues: FormValue<T>[] = [...intersection, ...disjunction].map(
            el => new FormValue(el, FormValueState.INDETERMINATE)
        );

        return indeterminateFormValues;
    }

    public mergeList<T>(list: T[][]): FormValue<T>[] {
        const intersection: T[] = this.intersection(list);
        const disjunction: T[] = this.inverseIntersection(list);

        const intersectionFormValues: FormValue<T>[] = intersection.map(el => new FormValue(el, FormValueState.SET));

        const disjunctionFormValues: FormValue<T>[] = disjunction.map(
            el => new FormValue(el, FormValueState.INDETERMINATE)
        );

        return [...intersectionFormValues, ...disjunctionFormValues];
    }

    public mergeSingleToSingle<T>(list: T[]): FormValue<T> {
        if (!list.length) {
            throw Error('Must supply list');
        }
        const first: T | undefined = list.find(item => isNonNullSimpleFieldValue(item));

        if (typeof first === 'undefined') {
            return new FormValue(list[0], FormValueState.UNSET);
        }

        if (list.every(el => isEqual(el, first))) {
            return new FormValue(first, FormValueState.SET);
        }
        return new FormValue(first, FormValueState.INDETERMINATE);
    }

    public mergeSingleToMultiple<T>(list: (T | null)[]): FormValue<T | null>[] {
        if (!list.length) {
            throw Error('Must supply list');
        }
        const unique: (T | null)[] = this.unique(list);
        if (unique.length === 0 || (unique.length === 1 && !unique[0])) {
            return [];
        } else if (unique.length === 1) {
            return unique.map(el => new FormValue(el, FormValueState.SET));
        } else {
            return unique.map(el => new FormValue(el, FormValueState.INDETERMINATE));
        }
    }

    private intersection<T>(arrays: T[][]): T[] {
        return arrays.reduce(
            (previousValues, currentValues) =>
                previousValues.filter(previousValue =>
                    currentValues.find(currentValue => JSON.stringify(currentValue) === JSON.stringify(previousValue))
                ),
            arrays[0]
        );
    }

    private unique<T>(array: T[]): T[] {
        return array.filter(
            (value, index, self) => self.findIndex(el => JSON.stringify(value) === JSON.stringify(el)) === index
        );
    }

    private inverseIntersection<T>(arrays: T[][]): T[] {
        const all: T[] = arrays.flat(1);
        const allNoDupes: T[] = this.unique(all);
        const intersection: T[] = this.intersection(arrays);
        return allNoDupes.filter(el => !intersection.includes(el));
    }

    public setProperty<
        F extends keyof NonNullable<PartialMetadata>,
        P extends keyof NonNullable<NonNullable<PartialMetadata>[F]>
    >(
        family: F,
        property: P,
        record: PartialRecord,
        formValue:
            | FormValue<NonNullable<NonNullable<PartialMetadata>[F]>[P]>
            | FormValue<NonNullable<NonNullable<PartialMetadata>[F]>[P]>[]
    ): PartialRecord {
        type propertyType = NonNullable<NonNullable<PartialMetadata>[F]>[P];
        const formValueArray: FormValue<propertyType>[] = formValue instanceof Array ? formValue : [formValue];

        for (const formValueElement of formValueArray) {
            if (formValueElement.state === FormValueState.UNSET) {
                record.set(family, property, formValueElement.value);
            }
            if (formValueElement.state === FormValueState.SET) {
                record.set(family, property, formValueElement.value);
            }
            if (formValueElement.state === FormValueState.INDETERMINATE) {
                // ignore
            }
        }

        return record;
    }

    public mergeSubProperty<R>(current: R | R[], formValue: FormValue<R> | FormValue<R>[]): R[] {
        let currentArray: R | R[] = current instanceof Array ? [...current] : [current];
        const formValueArray: FormValue<R>[] = formValue instanceof Array ? [...formValue] : [formValue];

        for (const formValueEl of formValueArray) {
            if (formValueEl.state === FormValueState.SET) {
                if (!currentArray.includes(formValueEl.value)) {
                    currentArray.push(formValueEl.value);
                }
            } else if (formValueEl.state === FormValueState.UNSET) {
                currentArray = currentArray.filter(val => formValueEl.value !== val);
            } else if (formValueEl.state === FormValueState.INDETERMINATE) {
                // ignore
            }
        }

        return currentArray;
    }
}
